/**
 * OrderList — clickable sidebar showing the song's pattern sequence.
 *
 * Phase 1 step 8: interactive.
 *   - Click an entry to select it (the pattern grid updates to that
 *     pattern; cursor moves there too).
 *   - The playhead highlight (cyan ring) is visually distinct from
 *     the selection highlight (filled background).
 *   - Bottom toolbar: < > to cycle pattern at selected slot,
 *     + Row to append a slot, × Del to remove the selected slot,
 *     + Pat to add a new empty pattern to the song.
 */

interface Props {
  orderList: number[];
  /** The currently-playing order index (from the scheduler), or null when stopped. */
  currentOrderIndex: number | null;
  /** The user's selected slot (for viewing/editing). */
  selectedOrderIndex: number;
  /** Total number of patterns that exist in the song. */
  patternCount: number;
  onSelect: (orderIndex: number) => void;
  onCyclePattern: (delta: number) => void;
  onAddRow: () => void;
  onRemoveRow: () => void;
  onAddPattern: () => void;
  /** True when a song is actively playing; disables destructive controls. */
  playing: boolean;
}

export function OrderList({
  orderList,
  currentOrderIndex,
  selectedOrderIndex,
  patternCount,
  onSelect,
  onCyclePattern,
  onAddRow,
  onRemoveRow,
  onAddPattern,
  playing,
}: Props) {
  const canRemove = orderList.length > 1 && !playing;

  return (
    <div className="flex w-32 shrink-0 flex-col rounded border border-nes-cyan/30 bg-nes-black font-mono text-[11px] leading-tight">
      <header className="flex items-center justify-between border-b border-nes-cyan/20 bg-nes-dim px-2 py-1">
        <span className="text-[10px] uppercase tracking-wider text-nes-white/40">Order</span>
        <span className="text-[10px] text-nes-white/40">{orderList.length} rows</span>
      </header>

      <div className="flex max-h-72 flex-col overflow-y-auto">
        {orderList.map((patternIndex, orderIndex) => {
          const isPlaying = orderIndex === currentOrderIndex;
          const isSelected = orderIndex === selectedOrderIndex;
          const bg = isPlaying
            ? 'bg-nes-cyan/30 text-nes-cyan'
            : isSelected
              ? 'bg-nes-white/[0.08] text-nes-white'
              : 'text-nes-white/70 hover:bg-nes-white/[0.04]';
          return (
            <button
              key={orderIndex}
              onClick={() => onSelect(orderIndex)}
              className={`flex items-center gap-2 px-2 py-[2px] text-left ${bg}`}
            >
              <span className="text-nes-white/40">{orderIndex.toString().padStart(2, '0')}</span>
              <span className="text-nes-white/30">│</span>
              <span>{patternIndex.toString().padStart(2, '0')}</span>
            </button>
          );
        })}
      </div>

      <div className="border-t border-nes-cyan/20 bg-nes-dim/50 p-2">
        <div className="mb-2 flex items-center justify-between gap-1">
          <button
            onClick={() => onCyclePattern(-1)}
            disabled={playing}
            className="rounded border border-nes-cyan/30 px-2 py-[1px] text-nes-cyan hover:bg-nes-cyan/10 disabled:cursor-not-allowed disabled:opacity-30"
            title="Previous pattern"
          >
            ‹
          </button>
          <span className="text-[10px] text-nes-white/50">
            pat {(orderList[selectedOrderIndex] ?? 0).toString().padStart(2, '0')}
          </span>
          <button
            onClick={() => onCyclePattern(1)}
            disabled={playing}
            className="rounded border border-nes-cyan/30 px-2 py-[1px] text-nes-cyan hover:bg-nes-cyan/10 disabled:cursor-not-allowed disabled:opacity-30"
            title="Next pattern"
          >
            ›
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1 text-[10px]">
          <button
            onClick={onAddRow}
            disabled={playing}
            className="rounded border border-nes-cyan/30 px-1 py-[1px] text-nes-cyan hover:bg-nes-cyan/10 disabled:cursor-not-allowed disabled:opacity-30"
            title="Append a new order row"
          >
            + row
          </button>
          <button
            onClick={onRemoveRow}
            disabled={!canRemove}
            className="rounded border border-nes-red/30 px-1 py-[1px] text-nes-red hover:bg-nes-red/10 disabled:cursor-not-allowed disabled:opacity-30"
            title="Remove the selected order row"
          >
            × del
          </button>
          <button
            onClick={onAddPattern}
            disabled={playing}
            className="col-span-2 rounded border border-nes-white/30 px-1 py-[1px] text-nes-white/80 hover:bg-nes-white/5 disabled:cursor-not-allowed disabled:opacity-30"
            title="Create a new empty pattern"
          >
            + new pattern ({patternCount} exist)
          </button>
        </div>
      </div>
    </div>
  );
}
